<!-- @format -->

<template>
    <div class="gst-room-offer">
        <div
            class="gst-room-offer__benefits"
            :data-test-id="'room-offer-benefits'">
            <div
                v-if="isWithMealPlan"
                class="benefit">
                <IconCheckCircle
                    class="gst-svg-icon__rounded"
                    width="18"
                    height="18" />
                <p>{{ rate.mealPlan | mealPlan }}</p>
            </div>
            <CancellationPolicies
                v-if="cancellationPolicies"
                :data="cancellationPolicies"
                :currency="currency" />
        </div>
        <div class="gst-room-offer__bundle-price-container">
            <div class="gst-room-offer__bundle-price text-left">
                <div class="col col-12 d-flex flex-column justify-start pa-0">
                    <VClamp
                        autoresize
                        :max-lines="2"
                        :title="bundleProductsWithCurrentRate | bundleProductsQuantity"
                        class="bundle-products">
                        {{ bundleProductsWithCurrentRate | bundleProductsQuantity }}
                    </VClamp>
                    <PriceDetail
                        show-tax-on-newline
                        :gift-card="giftCard"
                        :all-inclusive-pricing="allInclusivePricing"
                        :total-prices="bundleProductsTotals"
                        :discounted-amount="bundleProductsTotals.discountedAmount"
                        :currency="currency"
                        :price-per-night="pricePerNight"
                        :points="rate.redeemPoints"
                        :points-per-night="rate.redeemPointsPerNight"
                        class="my-2" />
                    <LoyaltyInfoChip
                        :show-min-max="!userIsAuth"
                        :points-info="pointsInfo"
                        :lowest-tier-status="
                            $t('shared:components.bonusPoints.loyaltyInfoChip.regularMember')
                        "
                        :highest-tier-status="
                            $t('shared:components.bonusPoints.loyaltyInfoChip.memberWithStatus')
                        " />
                </div>
                <RoomOfferFees
                    v-if="fees"
                    class="col col-12 flex-column align-center pa-0 pr-1 pa-md-0 mt-2 mt-xs-3"
                    :all-inclusive-pricing="allInclusivePricing"
                    :total-included="rate.totals.totalIncludedFeeAmount"
                    :bundle-includes-taxes-and-fees="bundleProductsTotals.includesTaxesAndFees"
                    :currency="currency"
                    :fees="fees" />
            </div>
            <BaseButton
                :placeholder="selectButtonText"
                class="gst-select-button"
                color="primary"
                :disabled="selected"
                @click="$emit('click')" />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VClamp from 'vue-clamp';
import { isWithoutMealPlan } from '@core/utils/roomRateMealPlanUtils';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import IconCheckCircle from '@tenant/app/assets/icons/check_round.svg';
import LoyaltyInfoChip from '@tenant/app/components/chips/LoyaltyInfoChip.vue';
import PriceDetail from '@tenant/app/modules/hotel/components/PriceDetail.vue';
import RoomOfferFees from './RoomOfferFees.vue';
import CancellationPolicies from './CancellationPolicies.vue';

export default {
    name: 'RoomOffer',
    components: {
        VClamp,
        IconCheckCircle,
        CancellationPolicies,
        PriceDetail,
        RoomOfferFees,
        LoyaltyInfoChip,
    },
    props: {
        rate: {
            type: Object,
            default: function () {
                return null;
            },
        },
        bundleProducts: {
            type: Object,
            default: function () {
                return CreateBundleProductsModel();
            },
        },
        selected: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeSlide: 0,
            displayNavigation: false,
        };
    },
    computed: {
        ...mapGetters({
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
            userIsAuth: 'user/isAuth',
        }),
        selectButtonText() {
            return this.selected
                ? this.$t('_common:buttons.selected')
                : this.$t('_common:buttons.select');
        },
        allInclusivePricing() {
            return this.rate.allInclusivePricing;
        },
        bundleProductsWithCurrentRate() {
            const ret = this.bundleProducts._clone();

            ret.setHotelReservationRateForSelectedRoom(this.rate);

            return ret;
        },
        bundleProductsTotals() {
            return this.bundleProductsWithCurrentRate.getTotals();
        },
        pricePerNight() {
            return this.rate.pricePerNight;
        },
        currency() {
            return this.rate.currency;
        },
        cancellationPolicies() {
            return this.rate.cancellationPolicies;
        },
        giftCard() {
            return this.rate.giftCard;
        },
        showGiftCard() {
            return this.giftCard ? this.giftCard.amount : 0;
        },
        fees() {
            return this.rate.fees;
        },
        room() {
            return this.bundleProductsWithCurrentRate.hotelReservation.hotel.selectedRoom;
        },
        isWithMealPlan() {
            return !isWithoutMealPlan(this.rate.mealPlan);
        },
        pointsInfo() {
            return {
                minimum: this.redeemPointsToggle
                    ? this.rate?.redeemPointsRange?.minimum
                    : this.rate?.earnPointsRange?.minimum,
                maximum: this.redeemPointsToggle
                    ? this.rate?.redeemPointsRange?.maximum
                    : this.rate?.earnPointsRange?.maximum,
                actual: this.redeemPointsToggle ? this.rate?.redeemPoints : this.rate.pointsEarned,
            };
        },
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-room-offer {
    display: flex;
    flex-direction: row;

    .gst-room-offer__benefits {
        display: flex;
        padding: theme-spacing(4);
        flex-direction: column;
        justify-content: center;
        flex-basis: 45%;
        min-width: 45%;
        border-right: 1px solid theme-color-hex('senary-darken-2');

        .benefit {
            display: flex;
            margin-bottom: theme-spacing(2);
            flex-direction: row;
            align-items: center;

            &:last-child {
                margin-bottom: theme-spacing(0);
            }

            p {
                margin-bottom: 0;
                color: theme-color('quaternary');
                font-size: font-size('xxs');
            }

            .gst-svg-icon__rounded {
                margin-right: theme-spacing(2);
                flex-shrink: 0;
                min-width: 16px;
                min-height: 16px;

                .gst-svg-icon {
                    fill: #316833;
                }
            }
        }
    }

    .gst-loyalty-info-chip {
        margin-top: 0 !important;

        .gst-loyalty-info-chip__section--minimum,
        .gst-loyalty-info-chip__section--maximum {
            p {
                font-weight: font-weight('medium');
            }
        }

        .gst-loyalty-info-chip__section--maximum .gst-bonus-points-chip__title {
            color: theme-color('quaternary');
        }
    }

    .gst-room-offer__bundle-price-container {
        display: flex;
        padding: theme-spacing(4);
        flex-grow: 1;
        justify-content: space-between;
        align-items: center;
        gap: theme-spacing(4);

        .gst-select-button {
            display: flex;
            font-size: font-size('s');
            font-weight: font-weight('bold');
            text-transform: unset;
        }

        .bundle-products {
            line-height: line-height('s');
            color: theme-color('tertiary');
            font-size: font-size('xxs');
        }
    }

    @include mobile-only {
        flex-direction: column;

        .gst-room-offer__benefits {
            padding-right: theme-spacing(4);
            padding-left: theme-spacing(3);
            border: 0;

            .benefit {
                p {
                    line-height: normal;
                    font-size: font-size('xxs');
                }
            }
        }

        .gst-room-offer__room-details {
            .amenities-container {
                width: 100%;
                padding-top: theme-spacing(3);
                max-width: unset;

                .gst-room-offer__name {
                    padding: theme-spacing(0);
                }

                .gst-room-offer__amenities {
                    height: auto;
                    padding: theme-spacing(0);
                }
            }
        }

        .gst-room-offer__bundle-price-container {
            width: 100%;
            padding-top: theme-spacing(0);
            padding-right: theme-spacing(3);
            padding-left: theme-spacing(3);
            max-width: unset;

            .gst-select-button {
                padding: 8px;
                font-weight: 600;
                float: right;
            }

            .bundle-products {
                text-align: left;
            }
        }
    }
}
</style>
